<template>
    <sidebar-left>
        <slot slot="sidebar-left">
            <SideMenu :SideMenuLinks="sideMenuLinks"></SideMenu>
        </slot>
        <slot slot="main">
            <v-loading type="global" :data="CustomerUsers"></v-loading>
            <div class="u-max-700 u-center-block">
                <page-header>
                    <slot slot="title">{{ $t('customer.account.users.title') }}</slot>
                    <slot slot="description">
                        <p v-html="UsersIntro"></p>
                    </slot>
                </page-header>

                <div class="o-wrapper o-wrapper--center">
                    <section class="o-page-row--medium">

                        <h3 class="u-pull-left">{{ $t('customer.account.users.subtitle') }}</h3>
                        <div class="u-pull-right">
                            <v-add-user
                                    @userAdded="addNewUser"
                                    :customerKey="customerKey"
                                    :roleOptions="[
                                        { value: 'Klant', content: $t('users.roles.administrator')},
                                        { value: 'MarketingKlant', content: 'Marketing'},
                                        { value: 'PersoneelKlant', content: $t('users.roles.personnel')}
                                    ]">
                            </v-add-user>
                        </div>

                        <table-component v-if="CustomerUsers.length" class="o-page-row" table-class="c-table c-table--small" :data="CustomerUsers" :show-caption="false" :show-filter="false">

                            <table-column key="LastName" :label="$t('customer.account.users.columns.name')" :sortable="false">
                                <template slot-scope="row">
                                    {{row.LastName}}
                                </template>
                            </table-column>
                            <table-column key="FirstName" :label="$t('customer.account.users.columns.firstname')" :sortable="false">
                                <template slot-scope="row">
                                    {{row.FirstName}}
                                </template>
                            </table-column>
                            <table-column key="Email" :label="$t('customer.account.users.columns.email')" :sortable="false">
                                <template slot-scope="row">
                                    {{row.Email}}
                                </template>
                            </table-column>
                            <table-column key="Role" :label="$t('customer.account.users.columns.role')" :sortable="false">
                                <template slot-scope="row">
                                    {{row.Role}}
                                </template>
                            </table-column>
                            <table-column cell-class="u-text-nowrap" :label="$t('customer.account.users.columns.blocked')" :sortable="false" :filterable="false">
                                <template slot-scope="row">
                                    <div class="o-spacing-tiny">
                                        <v-switch v-if="row.Id !== currentUserId && !row.IsMainAccount" v-model="row.IsBlocked" name="blocked" @input="blockUser(row.Id, row.IsBlocked)"></v-switch>
                                    </div>
                                </template>
                            </table-column>
                            <table-column cell-class="u-text-nowrap u-text-align-center" :label="$t('customer.account.users.columns.reset')" :sortable="false" :filterable="false">
                                <template slot-scope="row">
                                    <button class="c-button c-button--tiny c-button--alpha" @click="resetPassword(row.Email)">
                                        <svgicon name="reset" class="svg-icon svg-fill" width="24px" height="24px"></svgicon>
                                    </button>
                                </template>
                            </table-column>
                            <table-column cell-class="u-text-nowrap u-text-align-center" :sortable="false" :filterable="false">
                                <template slot-scope="row">
                                    <router-link :to="{ name: 'CustomerAccountEditUser', query: { 'id': row.Id } }">
                                        <button class="c-button c-button--tiny c-button--alpha" v-if="row.Id !== currentUserId && !row.IsMainAccount">
                                            <svgicon name="user" class="svg-icon svg-fill" width="24px" height="24px"></svgicon>
                                        </button>
                                    </router-link>
                                </template>
                            </table-column>
                            <table-column cell-class="u-text-nowrap u-text-align-center" :sortable="false" :filterable="false">
                                <template slot-scope="row">
                                    <button class="c-button c-button--tiny c-button--alpha" v-if="row.Id !== currentUserId && !row.IsMainAccount" @click="deleteUser(row.Email)">
                                        <svgicon name="cross" class="svg-icon svg-fill" width="24px" height="24px"></svgicon>
                                    </button>
                                </template>
                            </table-column>

                        </table-component>
                        <div v-show="CustomerUsers.length" v-else class="u-font-large u-text-align-center u-padding">
                            Geen gebruikers gevonden voor deze klant<br>
                            <span class="u-font-default">Fout in de pagina? Neem contact op met de administrator.</span>
                        </div>

                    </section>
                </div>

            </div>
        </slot>
    </sidebar-left>
</template>

<script>
    import VLoading from '@/components/Loading'
    import PageHeader from '@/components/common/PageHeader'
    import SideMenu from '@/components/common/SideMenu'
    import SidebarLeft from '@/components/layout/SidebarLeft'
    import VSwitch from '@/components/form/Switch'
    import VAddUser from '@/components/form/userSelect/AddUser'
    import {mapGetters} from 'vuex'

    export default {
        components: {
            PageHeader,
            SideMenu,
            SidebarLeft,
            VSwitch,
            VAddUser,
            VLoading
        },
        data() {
            return {
            }
        },
        created() {
            this.fetchPage();
        },
        computed: {
            ...mapGetters({
                CustomerUsers: 'CustomerUsersOptions'
            }),
            customerKey() {
                return parseInt(this.$store.getters.userInfo.customerKey);
            },
            currentUserId() {
                return localStorage.getItem('ra_userid');
            },
            UsersIntro() {
                return this.$t('intro.customer_account_users');
            },
            sideMenuLinks() {
                let menu = [
                    {
                        title: 'customer.account.edit.title',
                        route: '/customer/account/edit',
                    }
                ];

                if(this.isCustomer) {
                    menu.push({
                        title: 'customer.account.users.title',
                        route: '/customer/account/users'
                    });
                    menu.push({
                        title: 'customer.account.addresses.title',
                        route: '/customer/account/addresses'
                    });
                }

                if(this.isCustomer || this.isMarketing) {
                    menu.push({
                        title: 'customer.account.history.title',
                        route: '/customer/account/history'
                    });

                    menu.push({
                        title: 'customer.account.backorders.title',
                        route: '/customer/account/backorders'
                    })
                }

                if(this.isCustomer) {
                    menu.push({
                        title: 'customer.account.invoices.title',
                        route: '/customer/account/invoices'
                    });
                }

                return menu;
            },
            isCustomer() {
                return this.$store.getters.isCustomer;
            },
            isPersoneel() {
                return this.$store.getters.isPersoneel;
            },
            isMarketing() {
                return this.$store.getters.isMarketing;
            }
        },
        methods: {
            fetchPage() {
                this.$store.dispatch('getCustomerUsersOptions', this.customerKey);
            },
            blockUser(id, state) {
                this.$store.dispatch('blockCustomerUser', {
                    id: this.customerKey,
                    userId: id,
                    body: {
                        IsBlocked: state
                    }
                });
            },
            addNewUser() {
                this.fetchPage();
            },
            resetPassword(email) {
                this.$store.dispatch('forgotPassword', {
                    Email: email
                }).then((response) => {
                    if (response.status > 300) {
                        this.$notify({
                            group: 'global',
                            title: this.$t('global.something_went_wrong'),
                            type: 'error',
                            duration: 5000
                        })
                    } else {
                        this.$notify({
                            group: 'global',
                            title: this.$t('customer.account.forgot.sent_no_instructions'),
                            type: 'success',
                            duration: 5000
                        });
                    }
                });
            },
            deleteUser(email) {
                const confirm = window.confirm(this.$t('customer.account.users.delete'));
                if (confirm) {
                    this.$store.dispatch('deleteUser', {
                        Email: email
                    }).then((response) => {
                        this.$notify({
                            group: 'global',
                            title: this.$t('customer.account.users.deleteSuccess'),
                            type: 'success',
                            duration: 5000
                        });
                        this.fetchPage();
                    });
                }
            }
        }
    }
</script>

<style lang="scss" scoped>

</style>



// WEBPACK FOOTER //
// src/views/customer/account/Users.vue